import { ImportedListing, ScrapeStatus } from '../../_types/imported-listing'
import { PreviewBadgeTone } from './types'

const URL_PATTERN = /^https?:\/\/[^\s]+\.[^\s]+/iu

/**
 * Validates that a value looks like a real URL we can attempt to scrape.
 *
 * @param {string} value
 * @returns {string | null}
 */
const validateUrl = (value: string): string | null => {
  const trimmed = value.trim()

  if (trimmed === '') {
    return 'Paste a listing URL to import.'
  }

  if (URL_PATTERN.test(trimmed) === false) {
    return "That doesn't look like a valid URL — make sure it starts with http(s)://."
  }

  return null
}

const formatBedrooms = (bedrooms: number | null): string | null => {
  if (bedrooms === null) {
    return null
  }

  if (bedrooms === 0) {
    return 'Studio'
  }

  return `${bedrooms} bed${bedrooms === 1 ? '' : 's'}`
}

const formatBathrooms = (bathrooms: number | null): string | null => {
  if (bathrooms === null) {
    return null
  }

  return `${bathrooms} bath${bathrooms === 1 ? '' : 's'}`
}

const formatPrice = (
  priceMonthly: number | null,
  priceCurrency: string,
): string | null => {
  if (priceMonthly === null) {
    return null
  }

  const symbol = priceCurrency === 'USD' ? '$' : '$'
  return `${symbol}${priceMonthly.toLocaleString('en-CA')}/mo`
}

const buildPreviewMeta = (listing: ImportedListing): string[] => {
  const parts: string[] = []

  const price = formatPrice(listing.priceMonthly, listing.priceCurrency)
  const beds = formatBedrooms(listing.bedrooms)
  const baths = formatBathrooms(listing.bathrooms)

  if (price !== null) {
    parts.push(price)
  }
  if (beds !== null) {
    parts.push(beds)
  }
  if (baths !== null) {
    parts.push(baths)
  }
  if (listing.city !== '') {
    parts.push(listing.city)
  }

  return parts
}

/**
 * Picks the visual tone for the preview status badge based on the scrape
 * status. Replaces a previous nested ternary so each branch reads
 * top-to-bottom.
 *
 * @param {ScrapeStatus | null} status - the resolved scrape status, or null when no preview yet
 * @returns {PreviewBadgeTone} the tone variant for the preview badge
 */
const resolvePreviewBadgeTone = (
  status: ScrapeStatus | null,
): PreviewBadgeTone => {
  if (status === null) {
    return 'success'
  }

  if (status === 'success') {
    return 'success'
  }

  if (status === 'partial') {
    return 'partial'
  }

  return 'warning'
}

/**
 * Picks the renter-facing label that sits inside the preview badge.
 * Returns an empty string when there's no preview yet.
 *
 * @param {ScrapeStatus | null} status - the resolved scrape status, or null when no preview yet
 * @returns {string} the badge label or '' when nothing to show
 */
const resolvePreviewBadgeLabel = (status: ScrapeStatus | null): string => {
  if (status === null) {
    return ''
  }

  if (status === 'success') {
    return 'Imported'
  }

  if (status === 'partial') {
    return 'Partial'
  }

  if (status === 'site-unknown') {
    return 'Unknown site'
  }

  return 'Limited'
}

export {
  validateUrl,
  formatBedrooms,
  formatBathrooms,
  formatPrice,
  buildPreviewMeta,
  resolvePreviewBadgeTone,
  resolvePreviewBadgeLabel,
}
